import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import {
  doesMemberHaveEffectiveInsuranceCoverage,
  isDateInRange,
  isEnrollmentEffective,
} from '@/utils';
import Skeleton from 'components/v2/Skeleton';
import { ActiveTag, InactiveTag } from 'components/v2/Tag/library';

/** This component renders a visual tag (either `ActiveTag` or `InactiveTag`)
 * indicating whether a member, enrollment, or coverage is currently eligible
 * for insurance based on their effective insurance status and date range.
 *
 * If the `skeleton` prop is `true`, a loading skeleton is displayed.
 * Eligibility status is determined as follows (in order of precedence):
 *   1. If `member` prop is provided, uses `doesMemberHaveEffectiveInsuranceCoverage(member)`
 *   2. If `enrollment` prop is provided, uses `isEnrollmentEffective(enrollment)`
 *   3. If `coverage` prop is provided, checks if current date is in coverage's start/end date range
 *
 * Props:
 * @param {object} props
 * @param {boolean} [props.skeleton] - If `true`, shows a loading skeleton instead of eligibility tag.
 * @param {object} [props.member] - Member object to evaluate insurance eligibility.
 * @param {object} [props.enrollment] - Enrollment object to evaluate insurance eligibility.
 * @param {object} [props.coverage] - Coverage object to evaluate insurance eligibility.
 *
 * @returns {JSX.Element} ActiveTag, InactiveTag, or Skeleton depending on eligibility/skeleton flag.
 */
const InsuranceEligibilityTag = ({
  skeleton,
  member,
  enrollment,
  coverage,
}) => {
  const isActive = useMemo(() => {
    try {
      if (member) {
        return doesMemberHaveEffectiveInsuranceCoverage(member);
      }

      if (enrollment) {
        return isEnrollmentEffective(enrollment);
      }

      if (coverage) {
        const startDate = coverage?.startDate ?? coverage?.coverageStartDate;
        const endDate = coverage?.endDate ?? coverage?.coverageEndDate;

        if (!startDate && !endDate) {
          return false;
        }

        return isDateInRange({ startDate, endDate });
      }

      return false;
    } catch (error) {
      return false;
    }
  }, [member, enrollment, coverage]);

  if (skeleton) {
    return <Skeleton variant="circular" width={80} height={28} />;
  }

  return isActive ? <ActiveTag /> : <InactiveTag />;
};

InsuranceEligibilityTag.propTypes = {
  skeleton: PropTypes.bool,
  member: PropTypes.object,
  enrollment: PropTypes.object,
  coverage: PropTypes.object,
};

export default InsuranceEligibilityTag;
